import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import DynamicTable from "../components/CampaignComponents/DynamicTable";
import Pagination from "../components/CampaignComponents/Pagination";
import { fetchCampaigns, fetchCollections } from "../components/hooks/api";
import { CreateCampaignSidebar } from "../components/CampaignComponents/CreateCampaignSidebar";
import  StartCampaignSlider  from '../components/CampaignComponents/StartCampaignSlider';
import Slider from '../components/CampaignComponents/Slider';
import Switch from '../components/CampaignComponents/Switch';
import Button from '../components/CampaignComponents/Button';
import { makeMultipleCalls } from '../components/hooks/api.ts';
import { useToast } from '../components/hooks/use-toast.tsx';

interface Campaign {
  id?: string;
  _id?: string;
  [key: string]: any; // Allow for dynamic properties
}

interface Collection {
  id: number | string;
  name: string;
}

interface CampaignsResponse {
  collection: string;
  data: Campaign[];
}

interface CollectionsResponse {
  collections: Collection[];
}

const CampaignPage = () => {
  const [isCreateSidebarOpen, setIsCreateSidebarOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<string >("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedRows, setSelectedRows] = useState<Campaign[]>([]);
  const [showCampaignSlider, setShowCampaignSlider] = useState(false);
  const { toast } = useToast();

  // Fetch collections dropdown data
  const collectionsQuery = useQuery({
    queryKey: ["collections"],
    queryFn: fetchCollections,
  });

  // Fetch campaign data with filters
  const campaignsQuery = useQuery({
    queryKey: ["campaigns", selectedCollection, searchQuery, currentPage, itemsPerPage],
    queryFn: () => fetchCampaigns({
      collection: selectedCollection !== "all" ? selectedCollection : undefined,
      search: searchQuery,
      page: currentPage,
      limit: itemsPerPage
    }),
  });

  const handleCreateCampaign = () => {
    setIsCreateSidebarOpen(true);
  };

  const closeCreateSidebar = () => {
    setIsCreateSidebarOpen(false);
    // Refetch campaigns after creating a new one
    campaignsQuery.refetch();
  };

  // const handleCollectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
  //   // Use the collection name, not ID
  //   const selectedOption = e.target.options[e.target.selectedIndex];
  //   setSelectedCollection(selectedOption.text === "All Collections" ? "all" : selectedOption.text);
  //   setCurrentPage(1); // Reset to first page when changing filters
  // };

  // const handleCollectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
  //   const selectedCollectionName=e.target.options[e.target.selectedIndex].text
  //   const selectedValue = e.target.value;
  //   setSelectedCollection(selectedValue === "All Collections" ? "all" : selectedValue);
  //   setCurrentPage(1); // Reset to first page when changing filters
  // };
  const [selectedCollectionName, setSelectedCollectionName] = useState('');

  const handleCollectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCollectionName=e.target.options[e.target.selectedIndex].text
    const selectedValue = e.target.value;
    setSelectedCollection(selectedValue === "All Collections" ? "all" : selectedValue);
    setSelectedCollectionName(selectedCollectionName); // Update the state variable
    setCurrentPage(1); // Reset to first page when changing filters
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when changing search
  };

  const handleClearFilters = () => {
    setSelectedCollection("all");
    setSearchQuery("");
    setCurrentPage(1);
    // Display toast message
    toast({
      title: "Filters cleared",
      description: "All filters have been reset",
    });
  };

  const handleRowSelect = (rows: Campaign[]) => {
    setSelectedRows(rows);
  };

  const handleStartCampaign = () => {
    if (selectedRows.length === 0) {
      toast({
        title: "No rows selected",
        description: "Please select at least one row to start the campaign.",
        variant: "destructive",
      });
      return;
    }
    setShowCampaignSlider(true);
  };

  const handleCampaignComplete = () => {
    setShowCampaignSlider(false);
    setSelectedRows([]);
    campaignsQuery.refetch();
  };

  // Calculate total pages from data
  const totalItems = campaignsQuery.data?.data?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Get current collection name for display
  const currentCollectionName = selectedCollection === "all" 
    ? "All Collections" 
    : selectedCollection;

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b bg-white p-6">
      <div className="max-w-8xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Campaign Management</h1>
            <p className="text-gray-500 mt-1">
              {totalItems} Total · Updated {campaignsQuery.isSuccess ? "just now" : "..."}
            </p>
          </div>
          <div className="flex space-x-3">
            {selectedRows.length > 0 && (
              <Button 
                onClick={handleStartCampaign}
                variant="secondary"
                className="flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2Z"/>
                  <path d="m9 8 6 4-6 4V8Z"/>
                </svg>
                Start Campaign ({selectedRows.length})
              </Button>
            )}
            <button 
              onClick={handleClearFilters}
              className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-gray-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              <span>Clear Filters</span>
            </button>
            <button
              className="bg-primary hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
              onClick={handleCreateCampaign}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Create Campaign
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div>
            <div className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input 
                placeholder="Search..." 
                className="flex-1 outline-none text-sm"
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          
          <div>
            <select
              value={selectedCollection.toString()}
              onChange={handleCollectionChange}
              className="h-10 w-full rounded-md border border-gray-300 px-3 bg-white"
            >
              <option value="all">All Collections</option>
              {collectionsQuery.isSuccess && collectionsQuery.data && collectionsQuery.data.map((collection) => (
                <option key={collection.name.toString()}  value={collection.id.toString()}>
                  {collection.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {showCampaignSlider && (
          <div className="mb-6">
            <StartCampaignSlider
              selectedRows={selectedRows}
              collectionName={selectedCollectionName}
              onComplete={handleCampaignComplete}
            />
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden transition-shadow duration-300 hover:shadow-lg">
          {campaignsQuery.isLoading ? (
            <div className="p-8 flex flex-col items-center">
              <div className="w-16 h-16 border-4 border-t-blue-500 border-blue-200 rounded-full animate-spin"></div>
              <p className="mt-4 text-gray-500">Loading data...</p>
            </div>
          ) : campaignsQuery.isError ? (
            <div className="p-8 text-center">
              <p className="text-red-500">Error loading data. Please try again.</p>
              <button 
                className="mt-4 px-4 py-2 border rounded-md hover:bg-gray-50"
                onClick={() => campaignsQuery.refetch()}
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              <div className="p-4 bg-gray-50 border-b flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-800">
                  {campaignsQuery.data?.collection || currentCollectionName} Collection
                </h2>
                <div className="text-sm text-gray-500">
                  {selectedRows.length > 0 ? (
                    <span>{selectedRows.length} row{selectedRows.length !== 1 ? 's' : ''} selected</span>
                  ) : (
                    <span>No rows selected</span>
                  )}
                </div>
              </div>
              
              <DynamicTable 
                data={campaignsQuery.data?.data || []} 
                excludeColumns={["vehicleId", "autolineVehicleId", "customerId"]} 
                onRowSelect={handleRowSelect}
              />
              
              {totalPages > 1 && (
                <Pagination 
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={goToPage}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onItemsPerPageChange={setItemsPerPage}
                />
              )}
            </>
          )}
        </div>
      </div>
      
      <CreateCampaignSidebar
        isOpen={isCreateSidebarOpen}
        onClose={closeCreateSidebar}
        collections={collectionsQuery.data || []}
      />
    </div>
  );
};

export default CampaignPage;

